<script lang="ts">
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		real_id,
	} from '../../../../helpers/interface_helpers';
	//Stores
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import {
		Alert,
		Button,
		Toggle,
		Checkbox,
		Input,
		Radio,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';
	import compareVersions from 'compare-versions';
	import { conditionsFunc, getErrorOrInfoMessageForMinAndMax } from 'src/helpers/utilities';
	import { DEVICE_MAX_POWER_DEFAULTS } from 'src/constants';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	export let device;

	const id = real_id(device.id);
	$: settingsStore = getDeviceSettings(id);
	let settings = readDeviceSettings(id);
	$: settings = $settingsStore ?? settings;
	const component = get_gen_2_component_channel_structure(device);

	const minVerHoldOff = '0.14.0'; //Minimum version for holdoff setting
	const supportedHoldOff =
		compareVersions(settings?.DeviceInfo?.ver.split('-')[0], minVerHoldOff) > -1; //comparing versions
	const holdOffBoundaries = { min: 0.1, max: 300 }; //holdoff boundaries

	let enable = settings[component].obstruction_detection.enable; //Obstruction detection enabled
	let action = settings[component].obstruction_detection.action; //Actions stop or reverse
	let direction = settings[component].obstruction_detection.direction; //Moving directions open, close, both
	let powerThreshold = settings[component].obstruction_detection.power_thr; //Power threshold
	let holdOff = supportedHoldOff ? settings[component].obstruction_detection.holdoff : undefined; //Holdoff time in seconds

	let cacheEnabled = enable;
	let cacheAction = action;
	let cacheDirection = direction;
	let cachePower = powerThreshold;
	let cacheHoldOff = holdOff;
	let maxPowerThreshold = DEVICE_MAX_POWER_DEFAULTS[device.type];
	let minPowerThreshold = 0;

	$: powerThresholdMinAndMaxError = getErrorOrInfoMessageForMinAndMax(
		powerThreshold,
		minPowerThreshold,
		maxPowerThreshold,
	).error;
	$: powerThresholdNotDecimalError = conditionsFunc(powerThreshold)
		? $format('scene_invalid_format_err_toast')
		: '';
	$: powerThresholdError = powerThresholdMinAndMaxError || powerThresholdNotDecimalError;

	let loading = false;

	const direction_options = ['open', 'close', 'both'];
	const _direction_options_i18n = [
		$format('option_on_opening') /**while opening*/,
		$format('option_on_closing') /**while closing*/,
		$format('option_on_moving') /**while moving*/,
	];
	const action_options = ['stop', 'reverse'];
	const _action_options_i18n = [
		$format('option_stop') /** Stop (immediately stop on obstacle)*/,
		$format('option_reverse') /** Reverse (stop and go back to fully open or close)*/,
	];

	const handleReq = async () => {
		if (
			(cacheEnabled !== enable ||
				cacheAction !== action ||
				cacheDirection !== direction ||
				cachePower !== powerThreshold ||
				cacheHoldOff !== holdOff) &&
			!holdOffError
		) {
			const params = {
				id: device.channel,
				config: {
					obstruction_detection: {
						action,
						direction,
						enable,
						power_thr: powerThreshold,
						holdoff: holdOff,
					},
				},
			};
			loading = true;
			const res: SettingRequest = await gen2Request(device, 'Cover.SetConfig', params);

			if (res.isok) {
				toast.open({
					type: 'success',
					title: $format('setting_scc_saved_toast'),
				});
				cacheEnabled = enable;
				cacheAction = action;
				cacheDirection = direction;
				cachePower = powerThreshold;
				cacheHoldOff = holdOff;
			} else {
				enable = cacheEnabled;
				action = cacheAction;
				direction = cacheDirection;
				powerThreshold = cachePower;
				holdOff = cacheHoldOff;
				toast.open({ type: 'danger', title: $format('error_global') });
			}
			loading = false;
		}
	};
	const handleDirectionChange = (index) => {
		direction = direction_options[index];
	};
	const handleActionChange = (index) => {
		action = action_options[index];
	};

	$: holdOffError = getErrorOrInfoMessageForMinAndMax(
		holdOff,
		holdOffBoundaries.min,
		holdOffBoundaries.max,
	).error;

	const onToggleChange = () => {
		enable = !enable;
		handleReq();
	};

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout && enable) {
			setStickyBottomAction({
				onClick: handleReq,
				loading,
				disabled: loading || !!powerThresholdError,
			});
		} else {
			setStickyBottomAction(null);
		}
	}
</script>

{#if $showNewLayout}
	<Toggle
		info={$format('obst_description', {
			default: `This feature allows your cover to stop or change direction if it encounters an obstacle while driving.`,
		})}
		size="m"
		class="mb-m"
		checked={enable}
		on:click={onToggleChange}
		id="checkbox-pad0tpc"
	>
		{$format('enable')}
	</Toggle>
	{#if enable}
		<Text styleType="body-small" class="mb-s">
			{$format('moving_direction', { default: 'Moving direction:' })}
		</Text>
		{#each _direction_options_i18n as option, index}
			<Radio
				class={classMap({
					'mb-s': index < _direction_options_i18n.length - 1,
				})}
				size="m"
				selected={direction_options.indexOf(direction) === index}
				value={true}
				on:click={() => handleDirectionChange(index)}
				id="radio-kqdhlqn">{option}</Radio
			>
		{/each}

		<Text styleType="body-small" class="mt-m mb-s">
			{$format('type_action', { default: 'Action' })}
		</Text>
		{#each _action_options_i18n as _action, index}
			<Radio
				class="mb-s"
				size="m"
				selected={action_options.indexOf(action) === index}
				value={true}
				on:click={() => handleActionChange(index)}
				id="radio-by7zyin">{_action}</Radio
			>
		{/each}
		<Text styleType="body-small" class="mt-s mb-s">
			{$format('max_power_threshold', { default: `Max power threshold:` })}
		</Text>
		<Input
			type="number"
			size="m"
			bind:value={powerThreshold}
			on:enter={!powerThresholdError ? handleReq : undefined}
			error={powerThresholdError}
			id="input-jeeqcg6"
			aria-label={$format('max_power_threshold', {
				default: `Max power threshold`,
			})}
		/>
		{#if supportedHoldOff}
			<Text styleType="body-small" class="mt-m mb-s">
				{$format('hold_off_time', { default: `Hold off time (in seconds):` })}
			</Text>
			<Input
				type="number"
				size="m"
				bind:value={holdOff}
				error={holdOffError}
				min={holdOffBoundaries.min}
				max={holdOffBoundaries.max}
				step={0.1}
				on:enter={handleReq}
				id="input-z390yhl"
				aria-label={$format('hold_off_time', {
					default: `Hold off time (in seconds)`,
				})}
			/>
		{/if}
	{/if}
{:else}
	<Alert
		heading={$format('label_info')}
		isHeadingBold
		class="mb-s"
		message={$format('obst_description', {
			default: `This feature allows your cover to stop or change direction if it encounters an obstacle while driving.`,
		})}
	/>
	<Checkbox size="m" class="mb-s" bind:checked={enable} id="checkbox-pad0tpc"
		>{$format('enable')}</Checkbox
	>
	<Text styleType="body-small" class="mb-s">
		{$format('moving_direction', { default: 'Moving direction:' })}
	</Text>
	{#each _direction_options_i18n as option, index}
		<Radio
			size="m"
			selected={direction_options.indexOf(direction) === index}
			value={true}
			on:click={() => handleDirectionChange(index)}
			id="radio-kqdhlqn">{option}</Radio
		>
	{/each}

	<Text styleType="body-small" class="mb-s">{$format('type_action', { default: 'Action' }) + ':'}</Text
	>
	{#each _action_options_i18n as _action, index}
		<Radio
			size="m"
			selected={action_options.indexOf(action) === index}
			value={true}
			on:click={() => handleActionChange(index)}
			id="radio-by7zyin">{_action}</Radio
		>
	{/each}
	<Text styleType="body-small" class="mb-s">
		{$format('max_power_threshold', { default: `Max power threshold:` })}
	</Text>
	<Input
		class="mb-s"
		type="number"
		size="m"
		bind:value={powerThreshold}
		on:enter={!powerThresholdError ? handleReq : undefined}
		error={powerThresholdError}
		id="input-jeeqcg6"
		aria-label={$format('max_power_threshold', {
			default: `Max power threshold`,
		})}
	/>
	{#if supportedHoldOff}
		<Text styleType="body-small" class="mb-s">
			{$format('hold_off_time', { default: `Hold off time (in seconds):` })}
		</Text>
		<Input
			class="mb-s"
			type="number"
			size="m"
			bind:value={holdOff}
			error={holdOffError}
			min={holdOffBoundaries.min}
			max={holdOffBoundaries.max}
			step={0.1}
			on:enter={handleReq}
			id="input-z390yhl"
			aria-label={$format('hold_off_time', {
				default: `Hold off time (in seconds)`,
			})}
		/>
	{/if}
	<Button
		{loading}
		disabled={loading || powerThresholdError}
		size="m"
		width="full"
		on:click={handleReq}>{$format('button_save')}</Button
	>
{/if}
